import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Loader2, Save, Eye, Send, Clock, MessageCircle, Mail, Smartphone, Monitor } from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';

const Field = ({ label, value, onChange, rows }) => (
  <div>
    <label className="text-xs text-white/50 uppercase tracking-wide">{label}</label>
    {rows ? (
      <Textarea value={value || ''} onChange={e => onChange(e.target.value)} rows={rows} className="bg-black/40 border-[#333] text-white text-sm mt-1" />
    ) : (
      <Input value={value || ''} onChange={e => onChange(e.target.value)} className="bg-black/40 border-[#333] text-white text-sm mt-1" />
    )}
  </div>
);

const TrialCommsTab = () => {
  const [config, setConfig] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sampleAge, setSampleAge] = useState(8);
  const [mobileView, setMobileView] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [sendingTest, setSendingTest] = useState(false);

  const load = useCallback(async () => {
    const [c, t] = await Promise.all([
      api.get('/admin/trial-comms/config'),
      api.get('/admin/trial-comms/templates'),
    ]);
    setConfig(c.data);
    setTemplates(t.data.templates || []);
    if (!selectedId && t.data.templates?.length) setSelectedId(t.data.templates[0].class_id);
  }, [selectedId]);

  useEffect(() => { load().catch(() => toast.error('Failed to load trial comms settings')); }, []); // eslint-disable-line

  const tpl = templates.find(t => t.class_id === selectedId);
  const setTpl = (field, val) => setTemplates(ts => ts.map(t => t.class_id === selectedId ? { ...t, [field]: val } : t));
  const setCfg = (field, val) => setConfig(c => ({ ...c, [field]: val }));
  const setGroup = (i, field, val) => setConfig(c => ({
    ...c, age_groups: c.age_groups.map((g, gi) => gi === i ? { ...g, [field]: val } : g),
  }));

  const saveAll = async () => {
    setSaving(true);
    try {
      await api.put('/admin/trial-comms/config', config);
      if (tpl) await api.put(`/admin/trial-comms/templates/${tpl.class_id}`, tpl);
      toast.success('Saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const runPreview = async () => {
    if (!selectedId) return;
    setPreviewLoading(true);
    try {
      const res = await api.post('/admin/trial-comms/preview', { class_id: selectedId, sample_age: Number(sampleAge) });
      setPreview(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Preview failed');
    } finally { setPreviewLoading(false); }
  };

  const sendTest = async () => {
    if (!testTo.includes('@')) { toast.error('Enter a valid test email address'); return; }
    setSendingTest(true);
    try {
      const res = await api.post('/admin/trial-comms/test-email', { to: testTo, class_id: selectedId, sample_age: Number(sampleAge) });
      toast.success(res.data.message);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test send failed');
    } finally { setSendingTest(false); }
  };

  if (!config) return <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-[#FF007F]" /></div>;

  return (
    <div className="space-y-6" data-testid="trial-comms-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-xl font-bold text-white">Trial Emails &amp; WhatsApp Templates</h2>
        <Button onClick={saveAll} disabled={saving} className="bg-[#FF007F] hover:bg-[#d60068]" data-testid="save-trial-comms-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save Changes
        </Button>
      </div>

      {/* Age group times */}
      <Card className="bg-[#111] border-[#333]">
        <CardHeader><CardTitle className="text-white text-base flex items-center gap-2"><Clock className="w-4 h-4 text-[#FFD700]" /> Saturday Full-Day Age-Group Times</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {config.age_groups.map((g, i) => (
            <div key={i} className="grid grid-cols-2 sm:grid-cols-5 gap-2 items-end">
              <Field label="Label" value={g.label} onChange={v => setGroup(i, 'label', v)} />
              <Field label="Min age" value={g.min_age} onChange={v => setGroup(i, 'min_age', Number(v) || 0)} />
              <Field label="Max age" value={g.max_age} onChange={v => setGroup(i, 'max_age', Number(v) || 0)} />
              <Field label="Arrival" value={g.arrival} onChange={v => setGroup(i, 'arrival', v)} />
              <Field label="Collection" value={g.collection} onChange={v => setGroup(i, 'collection', v)} />
            </div>
          ))}
          <div className="grid sm:grid-cols-2 gap-2">
            <Field label="Saturday venue name" value={config.saturday_venue_name} onChange={v => setCfg('saturday_venue_name', v)} />
            <Field label="Saturday venue address" value={config.saturday_venue_address} onChange={v => setCfg('saturday_venue_address', v)} />
          </div>
        </CardContent>
      </Card>

      {/* Shared texts */}
      <Card className="bg-[#111] border-[#333]">
        <CardHeader><CardTitle className="text-white text-base">Shared Wording (used by email AND WhatsApp)</CardTitle></CardHeader>
        <CardContent className="grid sm:grid-cols-2 gap-4">
          <Field label="Full-day welcome paragraph" value={config.fullday_intro} onChange={v => setCfg('fullday_intro', v)} rows={6} />
          <Field label="Enquiry WhatsApp (not yet booked)" value={config.enquiry_whatsapp} onChange={v => setCfg('enquiry_whatsapp', v)} rows={6} />
          <Field label="No-enrolment note" value={config.no_enrol_note} onChange={v => setCfg('no_enrol_note', v)} rows={3} />
          <Field label="No experience needed note" value={config.no_experience_note} onChange={v => setCfg('no_experience_note', v)} rows={3} />
          <Field label="What to wear (full day)" value={config.wear_fullday} onChange={v => setCfg('wear_fullday', v)} rows={3} />
          <Field label="What to bring (full day)" value={config.bring_fullday} onChange={v => setCfg('bring_fullday', v)} rows={3} />
          <Field label="What to wear (short classes)" value={config.wear_short} onChange={v => setCfg('wear_short', v)} rows={2} />
          <Field label="What to bring (short classes)" value={config.bring_short} onChange={v => setCfg('bring_short', v)} rows={2} />
        </CardContent>
      </Card>

      {/* Per-class template */}
      <Card className="bg-[#111] border-[#333]">
        <CardHeader><CardTitle className="text-white text-base">Class Templates</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {templates.map(t => (
              <button key={t.class_id} onClick={() => setSelectedId(t.class_id)}
                className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${selectedId === t.class_id ? 'bg-[#FF007F] border-[#FF007F] text-white' : 'border-[#333] text-white/60 hover:text-white'}`}
                data-testid={`class-tpl-${t.class_id}`}>
                {t.display_name}
              </button>
            ))}
          </div>
          {tpl && (
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Display name" value={tpl.display_name} onChange={v => setTpl('display_name', v)} />
              <div className="grid grid-cols-2 gap-2">
                <Field label="Day" value={tpl.day} onChange={v => setTpl('day', v)} />
                <Field label="Session time (own-time classes)" value={tpl.session_time} onChange={v => setTpl('session_time', v)} />
              </div>
              <Field label="Venue name" value={tpl.venue_name} onChange={v => setTpl('venue_name', v)} />
              <Field label="Venue address" value={tpl.venue_address} onChange={v => setTpl('venue_address', v)} />
              <Field label="What to expect" value={tpl.what_to_expect} onChange={v => setTpl('what_to_expect', v)} rows={3} />
              <Field label="Where to report on arrival (optional)" value={tpl.report_to} onChange={v => setTpl('report_to', v)} rows={3} />
              <div className="flex items-center gap-4 col-span-full text-sm text-white/70">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={!!tpl.saturday_program} onChange={e => setTpl('saturday_program', e.target.checked)} className="accent-[#FF007F]" />
                  Part of the Saturday full-day programme (uses age-group times)
                </label>
                {tpl.saturday_program && <Badge className="bg-[#7000FF]/20 text-[#b07aff] border-[#7000FF]/40">Full-day invite shown</Badge>}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Preview + test send */}
      <Card className="bg-[#111] border-[#333]">
        <CardHeader><CardTitle className="text-white text-base flex items-center gap-2"><Eye className="w-4 h-4 text-cyan-400" /> Preview with Sample Booking</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-28"><Field label="Sample age" value={sampleAge} onChange={setSampleAge} /></div>
            <Button onClick={runPreview} disabled={previewLoading || !selectedId} variant="outline" className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10" data-testid="run-preview-btn">
              {previewLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />} Generate Preview
            </Button>
            <div className="flex items-end gap-2 ml-auto">
              <div className="w-56"><Field label="Send test email to" value={testTo} onChange={setTestTo} /></div>
              <Button onClick={sendTest} disabled={sendingTest} variant="outline" className="border-green-500/50 text-green-400 hover:bg-green-500/10" data-testid="send-test-email-btn">
                {sendingTest ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />} Send Test
              </Button>
            </div>
          </div>
          <p className="text-xs text-white/40">Previews use a sample student ("Lily, Sarah Example") — they never email real customers. Test emails go only to the address you enter.</p>
          {preview && (
            <div className="grid lg:grid-cols-2 gap-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-white/70 flex items-center gap-2"><Mail className="w-4 h-4 text-[#FF007F]" /> Branded Email</p>
                  <Button size="sm" variant="outline" className="border-[#444] text-white/60 h-7 text-xs" onClick={() => setMobileView(m => !m)}>
                    {mobileView ? <Monitor className="w-3 h-3 mr-1" /> : <Smartphone className="w-3 h-3 mr-1" />} {mobileView ? 'Desktop' : 'Mobile'}
                  </Button>
                </div>
                <div className="bg-white rounded-lg overflow-hidden mx-auto" style={{ width: mobileView ? 375 : '100%' }}>
                  <iframe title="email-preview" srcDoc={preview.email_html} style={{ width: '100%', height: 640, border: 0 }} data-testid="email-preview-iframe" />
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-white/70 flex items-center gap-2 mb-2"><MessageCircle className="w-4 h-4 text-[#25D366]" /> WhatsApp — Confirmed Booking</p>
                  <pre className="whitespace-pre-wrap bg-[#0b141a] border border-[#25D366]/30 rounded-lg p-4 text-sm text-[#e9edef] leading-relaxed" data-testid="whatsapp-preview">{preview.whatsapp_text}</pre>
                </div>
                <div>
                  <p className="text-sm text-white/70 flex items-center gap-2 mb-2"><MessageCircle className="w-4 h-4 text-amber-400" /> WhatsApp — Enquiry (not booked)</p>
                  <pre className="whitespace-pre-wrap bg-[#0b141a] border border-amber-500/30 rounded-lg p-4 text-sm text-[#e9edef] leading-relaxed" data-testid="enquiry-whatsapp-preview">{preview.enquiry_whatsapp_text}</pre>
                </div>
                {preview.facts?.needs_review && (
                  <p className="text-xs text-amber-300">⚠ This sample would be flagged for admin review: {preview.facts.review_reasons?.join('; ')}</p>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TrialCommsTab;
